/**
 * useVisitorPresence.js
 *
 * Writes the current visitor's presence to Firebase Realtime Database.
 * Runs for every visitor — anonymous or logged in.
 * Powers the Admin Live Support dashboard at /admin/live-support.
 *
 * RTDB path: /visitors/{visitorId}
 *
 * Fields written:
 *   id, visitorId       — stable ID persisted in localStorage
 *   isOnline            — true while on site; onDisconnect sets to false
 *   page                — current path (updates on navigation)
 *   sessionStart        — ms timestamp when they first arrived this session
 *   lastSeen            — ms timestamp, updated every 30s
 *   referrer            — document.referrer captured on session start
 *   navigationHistory   — ordered array of paths visited this session
 *   pageVisits          — ordered array of { path, enteredAt } for per-page dwell time
 *   userName, userEmail, userId, userRole — populated when logged in
 *   device              — "mobile" or "desktop"
 *   ip, country, city, region — geolocation from ipapi.co (best-effort, may be absent)
 */
import { useEffect, useRef } from "react";
import { rtdb } from "../firebaseConfig";
import { ref, set, onDisconnect, serverTimestamp, update, remove } from "firebase/database";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isLikelyBot } from "../utils/botDetect";

const HEARTBEAT_MS = 30_000;

function getOrCreateVisitorId() {
  let id = localStorage.getItem("s180_visitor_id");
  if (!id) {
    id = "v_" + Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
    localStorage.setItem("s180_visitor_id", id);
  }
  return id;
}

function detectDevice() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

// The owner and staff are never tracked or counted as visitors (owner, Nov 3 2026).
// Once an owner or staff account signs in on a device, that device is remembered,
// so it isn't counted even when signed out (e.g. the owner's phone on the public site).
const OWNER_EMAILS = new Set(["contact@thescope180.com"]);
const OWNER_UIDS = new Set(["SfXYMITYXVRkakPOaajHuk2WO7X2"]); // the owner's test learner
const OWNER_DEVICE_KEY = "scope180_owner_device";
function isOwnerDevice() {
  try { return localStorage.getItem(OWNER_DEVICE_KEY) === "1"; } catch { return false; }
}

export function useVisitorPresence() {
  const { currentUser, client, hasFullAccess } = useAuth();
  const location = useLocation();
  const visitorId = useRef(getOrCreateVisitorId()).current;
  const sessionStart = useRef(Date.now()).current;
  const navHistoryRef = useRef([]);
  const pageVisitsRef = useRef([]);
  const heartbeatRef = useRef(null);
  const initializedRef = useRef(false);
  // Crawlers get no presence record; initializedRef stays false so the other
  // effects below skip too.
  const isBot = useRef(isLikelyBot() || isOwnerDevice()).current;
  const ownerOff = useRef(false);

  // ── On mount: write initial presence record + set up onDisconnect ──────────
  useEffect(() => {
    if (isBot) return undefined;
    const visitorRef = ref(rtdb, `visitors/${visitorId}`);

    const base = {
      id: visitorId,
      visitorId,
      isOnline: true,
      page: location.pathname,
      sessionStart,
      lastSeen: Date.now(),
      referrer: document.referrer || null,
      navigationHistory: [location.pathname],
      pageVisits: [{ path: location.pathname, enteredAt: Date.now() }],
      device: detectDevice(),
      userAgent: navigator.userAgent,
    };

    // Write presence
    set(visitorRef, base).catch(() => {});

    // Auto-clean when tab closes / network drops
    onDisconnect(visitorRef).update({ isOnline: false, lastSeen: Date.now() });

    navHistoryRef.current = [location.pathname];
    pageVisitsRef.current = [{ path: location.pathname, enteredAt: Date.now() }];
    initializedRef.current = true;

    // Heartbeat to keep lastSeen fresh
    heartbeatRef.current = setInterval(() => {
      if (!ownerOff.current) update(visitorRef, { lastSeen: Date.now() }).catch(() => {});
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(heartbeatRef.current);
      if (!ownerOff.current) update(visitorRef, { isOnline: false, lastSeen: Date.now() }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId]);

  // ── Owner or staff signed in: stop tracking, delete this visitor record, remember the device
  useEffect(() => {
    if (!currentUser) return;
    const owner = OWNER_EMAILS.has((currentUser.email || "").toLowerCase()) || OWNER_UIDS.has(currentUser.uid) || hasFullAccess;
    if (!owner || ownerOff.current) return;
    ownerOff.current = true;
    try { localStorage.setItem(OWNER_DEVICE_KEY, "1"); } catch { /* private mode */ }
    if (isBot) return;
    clearInterval(heartbeatRef.current);
    initializedRef.current = false; // the page-change and identity effects below stop writing
    const visitorRef = ref(rtdb, `visitors/${visitorId}`);
    onDisconnect(visitorRef).cancel().catch(() => {});
    remove(visitorRef).catch(() => {});
  }, [currentUser, hasFullAccess, isBot, visitorId]);

  // ── Capture IP + geolocation (best-effort, once per session) ───────────────
  // Mirrors the Havlo widget's approach. Free, client-side, no key required.
  // Silently ignored if the lookup fails or is blocked — presence still works.
  useEffect(() => {
    if (isBot) return undefined;
    let cancelled = false;
    fetch("https://ipapi.co/json/")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || !initializedRef.current) return;
        const visitorRef = ref(rtdb, `visitors/${visitorId}`);
        if (ownerOff.current) return;
        update(visitorRef, {
          ip: data.ip || null,
          country: data.country_name || data.country || null,
          city: data.city || null,
          region: data.region || null,
        }).catch(() => {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visitorId, isBot]);

  // ── Track page changes ────────────────────────────────────────────────────
  useEffect(() => {
    if (!initializedRef.current) return;
    const visitorRef = ref(rtdb, `visitors/${visitorId}`);
    const path = location.pathname;

    // Append to history (avoid dupes for rapid re-renders)
    const hist = navHistoryRef.current;
    if (hist[hist.length - 1] !== path) {
      hist.push(path);
      if (hist.length > 50) hist.shift(); // cap at 50 entries

      // Timestamped visit so the dashboard can show dwell time per page
      const visits = pageVisitsRef.current;
      visits.push({ path, enteredAt: Date.now() });
      if (visits.length > 50) visits.shift();
    }

    update(visitorRef, {
      page: path,
      lastSeen: Date.now(),
      navigationHistory: [...navHistoryRef.current],
      pageVisits: [...pageVisitsRef.current],
    }).catch(() => {});
  }, [location.pathname, visitorId]);

  // ── Attach user identity when auth resolves ────────────────────────────────
  useEffect(() => {
    if (!initializedRef.current) return;
    const visitorRef = ref(rtdb, `visitors/${visitorId}`);

    if (currentUser) {
      update(visitorRef, {
        userId: currentUser.uid,
        userName: currentUser.displayName || client?.name || currentUser.email?.split("@")[0] || null,
        userEmail: currentUser.email || null,
        userRole: client?.role || null,
      }).catch(() => {});
    } else {
      update(visitorRef, {
        userId: null,
        userName: null,
        userEmail: null,
        userRole: null,
      }).catch(() => {});
    }
  }, [currentUser, client, visitorId]);
}

export default useVisitorPresence;
